import { CalendarMonth, InfoOutlined, ScienceOutlined, WhatsApp } from "@mui/icons-material";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useAtom } from "jotai";
import { FC, useEffect, useState } from "react";
import { modalAdmissionEmployee, userTypeAtom } from "../../../atoms/atom";
import gif from '../../../images/giphy.gif';
import { getLoading } from "../../../pages/execution-events/ExecutionEvents";
import { EmployeeService } from "../../../services/EmployeeService";
import { AdmissionEmployeeDTO } from "../../../types/AdmissionEmployeeDTO";
import { CreatedEmployeeDTO } from "../../../types/CreatedEmployeeDTO";
import { useUtils } from "../../../utils/utilFunctions";
import { useAlert } from "../../contextProviders/AlertProvider";
import { useDialog } from "../../contextProviders/DialogContext";
import "./AdmissionModal.scss";

const AdmissionModal: FC<{props: CreatedEmployeeDTO}> = ({props: employee}) => {
	const [open, setOpen] = useAtom(modalAdmissionEmployee);
	const [scheduling, setScheduling] = useState<AdmissionEmployeeDTO | null>(null);
	const [loadingSave, setLoadingSave] = useState(false);
	const [userType] = useAtom(userTypeAtom);
	const { showDialog } = useDialog();
	const { showAlert } = useAlert();
	const { formatStringDateReverse } = useUtils();
	const service = new EmployeeService();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const externalUser = "EXTERNO" === userType;

	const title = scheduling?.complementaries && scheduling.complementaries?.length
		? "Programe os exames admissionais de " + scheduling.name.split(" ")[0]
		: "Programe o exame admissional de " + scheduling?.name.split(" ")[0];
	const helpMessage = scheduling?.error
		? "Agendar com atendente"
		: "Escolher outro dia/horário";

	const handleClickWhatsApp = (name: string) => {
		const message = encodeURIComponent("Olá. Gostaria de agendar os exames admissionais do funcionário " + name)
		const link = isMobile
			? "https://wa.me/5551998826837?text=" + message
			: "https://web.whatsapp.com/send/?phone=5551998826837&text=" + message;
		window.open(link);
	};

	const handleClickSolicitationPdf = (id: number) => {
		window.open(`https://app.sgg.net.br/index.php?func=visual_solicitacao_editavel&id=${id}&chaves%5Bid%5D=${id}`);
	}

	const handleClickSolicitation = (id: string) => {
		window.open(`https://app.sgg.net.br/index.php?func=atendimento_medico&id_funcionario=${id}`);
	}

	useEffect(() => {
		if (!employee.sggId || employee.scheduled) {
			setScheduling(employee.scheduling);
			return;
		}
		const load = async () => {
			const response = await service.getAdmissionScheduling(employee.sggId);
			if (response && response.data) {
				setScheduling(response.data);
			}
		};
		load();
	}, [employee.id]);

	return (
		<Dialog
			open={open}
			onClose={() => setOpen(false)}
			id='modal-admission'
		>
			<DialogTitle>
				<img alt="" src={gif}/>
				{`Funcionário cadastrado ${employee.scheduled ? "e agendado " : ""}com sucesso`}
			</DialogTitle>
			<DialogContent id="admission-modal-content">
				{scheduling
					? <>
						{employee.scheduled ? <></> : <Typography variant="h5">{title}</Typography>}
						{scheduling.complementaries.length
							? <div>
								<Typography variant="h6">Exames complementares necessários para o cargo:</Typography>
								<ul>
									{scheduling.complementaries.map((item) => (
										<li><Typography>{item}</Typography></li>
									))}
								</ul>
							</div>
							: <></>
						}
						{scheduling.error
							? <Typography variant="h5">Entre em contato com um atendente para agendar os exames admissionais</Typography>
							: <Box className="scheduling-cards">
								{scheduling.schedulings.map((item) => (
									<div className="scheduling-card">
										<div className="scheduling-card-line">
											<Typography variant="h6">
												{item.typeScheduling === "CLINICAL"
													? <CalendarMonth color="secondary"/>
													: <ScienceOutlined color="secondary"/>
												}
												{item.name}
											</Typography>
										</div>
										<div className="scheduling-card-infos">
											<div className="scheduling-card-line"><h4>Data:</h4><p>{formatStringDateReverse(item.day)}</p></div>
											<div className="scheduling-card-line"><h4>Horário:</h4><p>{item.hour?.slice(0, 5)}</p></div>
											<div className="scheduling-card-line"><h4>Clínica:</h4><p>{item.suplier}</p></div>
											<div className="scheduling-card-line"><h4>Endereço:</h4><p>{item.address}</p></div>
											{item.examSolicitationId
												? <Button onClick={() => handleClickSolicitationPdf(item.examSolicitationId)}>Ver PDF da solicitação de exame</Button>
												: <></>
											}
										</div>
									</div>
								))}
							</Box>
						}
						{employee.scheduled
							? <Box sx={{width: "100%", display: "flex", justifyContent: "center"}}>
								<Button
									sx={{width: "30vh"}}
									variant="contained"
									onClick={() => handleClickSolicitation(employee.sggId)}
								>
									Visualizar atendimento
								</Button>
							</Box>
							: <></>
						}
					</>
					: getLoading()
				}
			</DialogContent>
			<DialogActions>
				{externalUser || employee.scheduled || employee.internallyScheduled
					? <></>
					: <>
						<IconButton onClick={() => showDialog("Detalhamento do algorítimo de agendamento", scheduling?.details || "")}>
							<InfoOutlined/>
						</IconButton>
						<Button
							variant="contained"
							color="warning"
							onClick={() => scheduleInternally(employee.id)}
						>
							Marcar como agendado internamente
						</Button>
					</>
				}
				{employee.scheduled
					? <></>
					: <>
						{externalUser
							? <Button id="whatsApp-chose" variant="contained" color="info" onClick={() => handleClickWhatsApp(scheduling?.name || "")}>
								<WhatsApp/> {helpMessage}
							</Button>
							: <></>
						}
						{scheduling?.error
							? <></>
							: <Button
								variant="contained"
								color="success"
								onClick={confirm}
							>
								{loadingSave
									? <CircularProgress sx={{color: "black"}} size={'25px'}/>
									: `Confirmar agendamento${scheduling?.complementaries.length ? "s" : ""}`
								}
							</Button>
						}
					</>
				}
			</DialogActions>
		</Dialog>
	);

	async function confirm() {
		if (!scheduling) return;
		setLoadingSave(true);
		const response = await service.confirmAdmissionScheduling(employee.sggId, scheduling);
		if (response?.status === 200) {
			showAlert("Agendamento confirmado com sucesso!");
		}
		setOpen(false);
		setLoadingSave(false);
		await new Promise(resolve => setTimeout(resolve, 2000));
		window.location.reload();
	}

	async function scheduleInternally(id: number) {
		await service.scheduleInternally(id);
		showAlert("Marcado como agendado internamente");
		setOpen(false);
	}
}

export default AdmissionModal